import { FC, useMemo } from "react";
import * as yup from "yup";
import { Lang } from "@brg/api";
import { CommonInput, CommonLabel, FInter, FlexBox, INPUT_TYPES } from "@brg/ui";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled, { css, useTheme } from "styled-components";
import { HolderContent } from "shared/components/ImagePreviewSection/ImagePreviewSection";
import {
    FileInput,
    FileInputWrapper,
} from "shared/modals/styles";
import { RadioBox, RadioCircle, RadioInputSource, RadioLabel } from "shared/components/Radio/styles";
import { FormFieldType } from "shared/modals/ApplicationEditor/constants";
import { CustomCheckbox } from "../../CustomCheckbox";
import { FlexInputContainer, LabelRedStar } from "../../../style";
import { getAdditionalInfoVal } from "shared/modals/ApplicationEditor/support";
import { UniversitySearchBar } from "shared/components/DropdownSearchBar/UniversitySearchBar";

const validateWithYupSchema = async (schema: yup.AnySchema, value: unknown) => {
    try {
        await schema.validate(value);
        return true as const;
    } catch (error) {
        return (error as yup.ValidationError).message;
    }
};

const hasUploadedFileValue = (value: unknown): boolean => {
    if (value === true) return true;
    if (value == null) return false;
    if (typeof FileList !== "undefined" && value instanceof FileList) return value.length > 0;
    if (typeof File !== "undefined" && value instanceof File) return true;
    if (typeof value === "object" && value !== null && "length" in value) {
        const n = Number((value as { length: number }).length);
        return Number.isFinite(n) && n > 0;
    }
    const files = (value as { target?: { files?: FileList } })?.target?.files;
    return !!files?.length;
};

const RadioWrapper = styled(FlexBox)<{ checked?: boolean }>`
    ${({ checked }) => checked && css`
        .radio-circle {
            &:after {
                content: "" !important;
                width: 5px !important;
                height: 5px !important;
                border-radius: 50% !important;
                background: ${({ theme }) => theme.projectEdit.radio.dot.color} !important;
            }
        }
    `};
`;

const Radio = (props: any) => {
    return (
        <RadioWrapper {...props} style={{ cursor: "pointer" }} className={props.checked ? "active" : ""}>
            <FlexBox style={{ position: "relative" }}>
                <RadioInputSource {...props} type="radio" />
                <RadioBox {...props}>
                    <RadioCircle className="radio-circle" {...props} />
                    <RadioLabel {...props}>
                        {props.title}
                    </RadioLabel>
                </RadioBox>
            </FlexBox>
        </RadioWrapper>
    );
};

export const FieldInput: FC<{ inputProps: any }> = ({ inputProps }) => {
    const theme = useTheme();
    const { t, i18n: { language } } = useTranslation();
    const { register: _register, control, validation, ...fieldProps } = inputProps ?? {};

    const fieldLabel = useMemo(() => {
        if (typeof fieldProps?.label === "string") return fieldProps.label;
        return Lang(fieldProps.label, language);
    }, [fieldProps?.label, language]);

    const fieldPlaceholder = useMemo(() => {
        if (typeof fieldProps?.placeholder === "string") return fieldProps.placeholder;
        return Lang(fieldProps.placeholder, language);
    }, [fieldProps?.placeholder, language]);

    const options = useMemo(
        () => fieldProps?.inlineVariants ?? fieldProps?.inlineVariants ?? [],
        [fieldProps?.options, fieldProps?.inlineVariants]
    );

    // {nominationsList?.length && !!onEducationChange && !!onSelectedEduChange && (
    //     <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start" }}>
    //         <CommonLabel 
    //             label={t('designer_edit.fields.education_place')} 
    //         />
    //       {/* <LegalsLabel>{t('designer_edit.fields.education_place')}</LegalsLabel> */}
    //       <EduSecetWrapper>
    //           <UniversitySearchBar
    //               onAddClick={onEducationChange}
    //               onChooseClick={onSelectedEduChange}
    //               editing
    //               placeholder=''
    //           />
    //       </EduSecetWrapper>
    //   </GridBox>
    // )}

    // if (inputProps?.isOldLegalCheckbox) {
    //     const labelText = Lang(inputProps.label ?? inputProps.header, language);
    //     const urlSplitRegex = /(https?:\/\/[^\s]+)/g;
    //     const parts = labelText.split(urlSplitRegex);

    //     return (
    //         <FlexBox direction="row" align="flex-start" gy={8} gyMobile={8}>
    //             <div style={{ flex: 1, minWidth: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
    //                 {parts.map((part, i) =>
    //                     /^(https?:\/\/[^\s]+)$/.test(part) ? (
    //                         <a key={i} href={part} target="_blank" rel="noopener noreferrer">
    //                             {part}
    //                         </a>
    //                     ) : (
    //                         <span key={i}>{part}</span>
    //                     )
    //                 )}
    //             </div>
    //             <Controller
    //                 name={inputProps?.id}
    //                 control={inputProps?.control}
    //                 defaultValue={false}
    //                 rules={{
    //                     validate: (value) => validateWithYupSchema(inputProps.validation, value),
    //                 }}
    //                 render={({ field: { onChange, value } }) => (
    //                     <CustomCheckbox
    //                         checked={!!value}
    //                         onChange={() => onChange(!value)}
    //                     />
    //                 )}
    //             />
    //         </FlexBox>
    //     );
    // }

    const getOptionTitle = (option: any) => {
        if (typeof option === "string") return option;
        return Lang(option, language);
        
    };

    if ([FormFieldType.Education].includes(fieldProps?.type)) {
        return (
            <Controller
            name={fieldProps?.id}
            control={control}
            defaultValue={false}
            rules={{
                validate: (value) => validateWithYupSchema(validation, value),
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <UniversitySearchBar
                    {...fieldProps}
                    label={t('designer_edit.fields.education_center')}
                    placeholder=''
                    required={fieldProps?.isRequired}
                    onChange={onChange}
                    value={value}
                />   
            )}
        /> 
        )
    }

    if ([FormFieldType.LegalDocument].includes(fieldProps?.type)) {
        const textBefore = getAdditionalInfoVal(fieldProps?.additionalInfo, 'textBefore');
        const textAfter = getAdditionalInfoVal(fieldProps?.additionalInfo, 'textAfter');
        const text = getAdditionalInfoVal(fieldProps?.additionalInfo, 'text');

        const normalizedText = (
            <>
                {Lang(textBefore, language)}
                {" "}
                <a href={fieldProps.url} target="_blank" rel="noopener noreferrer">
                    {Lang(text, language)}
                </a>
                {" "}
                {Lang(textAfter, language)}
                {<LabelRedStar hide={!fieldProps?.isRequired}>*</LabelRedStar>}
            </>
        );


        return (
            <Controller
                name={fieldProps?.id}
                control={control}
                defaultValue={false}
                rules={{
                    validate: (value) => validateWithYupSchema(validation, value),
                }}
                render={({ field: { onChange, value } }) => (
                    <FlexBox direction="row" align="center">
                        <CustomCheckbox
                            id={fieldProps?.id}
                            name={fieldProps?.id}
                            checked={!!value}
                            onChange={() => onChange(!value)}
                        />
                        <span className="checkbox-label">{normalizedText}</span>
                    </FlexBox>
                )}
            />
        );
    }

    if ([FormFieldType.File].includes(fieldProps?.type)) {
        return (
            <FlexBox direction="column" gy={12} gyMobile={12} width="100%">
                <CommonLabel required={fieldProps?.isRequired} label={fieldLabel} />
                <Controller
                    name={fieldProps?.id}
                    control={control}
                    rules={{
                        validate: (value) => validateWithYupSchema(validation, value),
                    }}
                    render={({ field: { onChange, value } }) => {
                        const hasFile = hasUploadedFileValue(value);
                        return (
                            <FileInputWrapper customBackgroundColor={theme.projectEdit.imgContainerBg}>
                                <FileInput
                                    type="file"
                                    onChange={(ev: any) => {
                                        const files = ev?.target?.files;
                                        onChange(files?.length ? files : null);
                                    }}
                                />
                                <HolderContent>
                                    <FInter.SmallText color={theme.sendProjectsModal.title}>
                                        {hasFile
                                            ? t("competition_edit.form.message.file_uploaded")
                                            : t("competition_edit.form.message.file_upload")}
                                    </FInter.SmallText>
                                </HolderContent>
                            </FileInputWrapper>
                        );
                    }}
                />
            </FlexBox>
        );
    }

    if (fieldProps?.type === FormFieldType.SelectOne && options.length > 0) {
        return (
            <FlexBox direction="row">
                <CommonLabel required={fieldProps?.isRequired} label={fieldLabel} />
                <Controller
                    name={fieldProps?.id}
                    control={control}
                    rules={{
                        validate: (value) => validateWithYupSchema(validation, value),
                    }}
                    render={({ field: { onChange, value: controlledValue } }) => (
                        <FlexBox direction="column" directionMobile="column" gy={8} gyMobile={8}>
                            {options.map((item: any, currentIndex: number) => {
                                const title = getOptionTitle(item);
                                const isChecked = JSON.stringify(controlledValue) === JSON.stringify(item);

                                return (
                                    <Radio
                                        key={`${fieldProps?.id}_${currentIndex}`}
                                        checked={isChecked}
                                        title={title}
                                        onChange={() => onChange(item)}
                                    />
                                );
                            })}
                        </FlexBox>
                    )}
                />
            </FlexBox>
        );
    }

    if (fieldProps?.type === FormFieldType.SelectMultiple && options.length > 0) {
        return (
            <FlexBox direction="row">
                <CommonLabel required={fieldProps?.isRequired} label={fieldLabel} />
                <Controller
                    name={fieldProps?.id}
                    control={control}
                    rules={{
                        validate: (value) => validateWithYupSchema(validation, value),
                    }}
                    render={({ field: { onChange, value = [] } }) => (
                        <FlexBox direction="column" directionMobile="column" gy={8} gyMobile={8}>
                            {options.map((item: any, currentIndex: number) => {
                                const itemString = JSON.stringify(item);
                                const selectedValues = Array.isArray(value) ? value : [];
                                const isChecked = selectedValues.some((selected: any) => JSON.stringify(selected) === itemString);

                                return (
                                    <FlexBox key={`${fieldProps?.id}_${currentIndex}`} direction="row" align="center">
                                        <CustomCheckbox
                                            value={isChecked ? 1 : 0}
                                            onChange={() => {
                                                if (isChecked) {
                                                    onChange(selectedValues.filter((selected: any) => JSON.stringify(selected) !== itemString));
                                                    return;
                                                }
                                                onChange([...selectedValues, item]);
                                            }}
                                        />
                                        <span className="checkbox-label">{getOptionTitle(item)}</span>
                                    </FlexBox>
                                );
                            })}
                        </FlexBox>
                    )}
                />
            </FlexBox>
        );
    }

    if (fieldProps?.type === FormFieldType.Phone) {
        return (
            <FlexInputContainer direction="row" directionMobile="column">
                <Controller
                    name={fieldProps?.id}
                    control={control}
                    rules={{
                        validate: (value) => validateWithYupSchema(validation, value),
                    }}
                    render={({ field: { onChange, value = "" } }) => (
                        <CommonInput
                            {...fieldProps}
                            type={INPUT_TYPES.phone}
                            globalLang={language}
                            onChange={onChange}
                            onBlur={() => null}
                            onFocus={() => null}
                            name={fieldProps?.id}
                            value={value ?? "+7"}
                            required={fieldProps?.isRequired}
                            label={fieldLabel}
                            placeholder={fieldPlaceholder}
                        />
                    )}
                />
            </FlexInputContainer>
        );
    }

    if (fieldProps?.type === FormFieldType.Date) {
        return (
            <Controller
                name={fieldProps?.id}
                control={control}
                rules={{
                    validate: (value) => validateWithYupSchema(validation, value),
                }}
                render={({ field: { onChange, value, ref } }) => (
                    <CommonInput
                        {...fieldProps}
                        ref={ref}
                        type="date"
                        label={fieldLabel}
                        required={fieldProps?.isRequired}
                        style={{ ...(fieldProps?.hidden ? { display: "none" } : {}) }}
                        placeholder={fieldPlaceholder}
                        value={value ?? ""}
                        onChange={onChange}
                    />
                )}
            />
        );
    }

    return (
        <Controller
            name={fieldProps?.id}
            control={control}
            rules={{
                validate: (value) => validateWithYupSchema(validation, value),
            }}
            render={({ field: { onChange, value, ref } }) => (
                <CommonInput
                    {...fieldProps}
                    ref={ref}
                    type={
                        fieldProps?.type === FormFieldType.Mail ||
                        fieldProps?.type === FormFieldType.AdditionalMail
                            ? "email"
                            : "text"
                    }
                    label={fieldLabel}
                    placeholder={fieldPlaceholder}
                    style={{ ...(fieldProps?.hidden ? { display: "none" } : {}) }}
                    required={fieldProps?.isRequired}
                    value={value ?? ""}
                    onChange={onChange}
                />
            )}
        />
    );
};import { FC, useMemo } from "react";
import * as yup from "yup";
import { Lang } from "@brg/api";
import { CommonInput, CommonLabel, FInter, FlexBox, INPUT_TYPES } from "@brg/ui";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled, { css, useTheme } from "styled-components";
import { HolderContent } from "shared/components/ImagePreviewSection/ImagePreviewSection";
import {
    FileInput,
    FileInputWrapper,
} from "shared/modals/styles";
import { RadioBox, RadioCircle, RadioInputSource, RadioLabel } from "shared/components/Radio/styles";
import { FormFieldType } from "shared/modals/ApplicationEditor/constants";
import { CustomCheckbox } from "../../CustomCheckbox";
import { FlexInputContainer, LabelRedStar } from "../../../style";
import { getAdditionalInfoVal } from "shared/modals/ApplicationEditor/support";
import { UniversitySearchBar } from "shared/components/DropdownSearchBar/UniversitySearchBar";

const validateWithYupSchema = async (schema: yup.AnySchema, value: unknown) => {
    try {
        await schema.validate(value);
        return true as const;
    } catch (error) {
        return (error as yup.ValidationError).message;
    }
};

const hasUploadedFileValue = (value: unknown): boolean => {
    if (value === true) return true;
    if (value == null) return false;
    if (typeof FileList !== "undefined" && value instanceof FileList) return value.length > 0;
    if (typeof File !== "undefined" && value instanceof File) return true;
    if (typeof value === "object" && value !== null && "length" in value) {
        const n = Number((value as { length: number }).length);
        return Number.isFinite(n) && n > 0;
    }
    const files = (value as { target?: { files?: FileList } })?.target?.files;
    return !!files?.length;
};

const RadioWrapper = styled(FlexBox)<{ checked?: boolean }>`
    ${({ checked }) => checked && css`
        .radio-circle {
            &:after {
                content: "" !important;
                width: 5px !important;
                height: 5px !important;
                border-radius: 50% !important;
                background: ${({ theme }) => theme.projectEdit.radio.dot.color} !important;
            }
        }
    `};
`;

const Radio = (props: any) => {
    return (
        <RadioWrapper {...props} style={{ cursor: "pointer" }} className={props.checked ? "active" : ""}>
            <FlexBox style={{ position: "relative" }}>
                <RadioInputSource {...props} type="radio" />
                <RadioBox {...props}>
                    <RadioCircle className="radio-circle" {...props} />
                    <RadioLabel {...props}>
                        {props.title}
                    </RadioLabel>
                </RadioBox>
            </FlexBox>
        </RadioWrapper>
    );
};

export const FieldInput: FC<{ inputProps: any }> = ({ inputProps }) => {
    const theme = useTheme();
    const { t, i18n: { language } } = useTranslation();
    const { register: _register, control, validation, ...fieldProps } = inputProps ?? {};

    const fieldLabel = useMemo(() => {
        if (typeof fieldProps?.label === "string") return fieldProps.label;
        return Lang(fieldProps.label, language);
    }, [fieldProps?.label, language]);

    const fieldPlaceholder = useMemo(() => {
        if (typeof fieldProps?.placeholder === "string") return fieldProps.placeholder;
        return Lang(fieldProps.placeholder, language);
    }, [fieldProps?.placeholder, language]);

    const options = useMemo(
        () => fieldProps?.inlineVariants ?? fieldProps?.inlineVariants ?? [],
        [fieldProps?.options, fieldProps?.inlineVariants]
    );

    // {nominationsList?.length && !!onEducationChange && !!onSelectedEduChange && (
    //     <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start" }}>
    //         <CommonLabel 
    //             label={t('designer_edit.fields.education_place')} 
    //         />
    //       {/* <LegalsLabel>{t('designer_edit.fields.education_place')}</LegalsLabel> */}
    //       <EduSecetWrapper>
    //           <UniversitySearchBar
    //               onAddClick={onEducationChange}
    //               onChooseClick={onSelectedEduChange}
    //               editing
    //               placeholder=''
    //           />
    //       </EduSecetWrapper>
    //   </GridBox>
    // )}

    // if (inputProps?.isOldLegalCheckbox) {
    //     const labelText = Lang(inputProps.label ?? inputProps.header, language);
    //     const urlSplitRegex = /(https?:\/\/[^\s]+)/g;
    //     const parts = labelText.split(urlSplitRegex);

    //     return (
    //         <FlexBox direction="row" align="flex-start" gy={8} gyMobile={8}>
    //             <div style={{ flex: 1, minWidth: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
    //                 {parts.map((part, i) =>
    //                     /^(https?:\/\/[^\s]+)$/.test(part) ? (
    //                         <a key={i} href={part} target="_blank" rel="noopener noreferrer">
    //                             {part}
    //                         </a>
    //                     ) : (
    //                         <span key={i}>{part}</span>
    //                     )
    //                 )}
    //             </div>
    //             <Controller
    //                 name={inputProps?.id}
    //                 control={inputProps?.control}
    //                 defaultValue={false}
    //                 rules={{
    //                     validate: (value) => validateWithYupSchema(inputProps.validation, value),
    //                 }}
    //                 render={({ field: { onChange, value } }) => (
    //                     <CustomCheckbox
    //                         checked={!!value}
    //                         onChange={() => onChange(!value)}
    //                     />
    //                 )}
    //             />
    //         </FlexBox>
    //     );
    // }

    const getOptionTitle = (option: any) => {
        if (typeof option === "string") return option;
        return Lang(option, language);
        
    };

    if ([FormFieldType.Education].includes(fieldProps?.type)) {
        return (
            <Controller
            name={fieldProps?.id}
            control={control}
            defaultValue={false}
            rules={{
                validate: (value) => validateWithYupSchema(validation, value),
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <UniversitySearchBar
                    {...fieldProps}
                    label={t('designer_edit.fields.education_center')}
                    placeholder=''
                    required={fieldProps?.isRequired}
                    onChange={onChange}
                    value={value}
                />   
            )}
        /> 
        )
    }

    if ([FormFieldType.LegalDocument].includes(fieldProps?.type)) {
        const textBefore = getAdditionalInfoVal(fieldProps?.additionalInfo, 'textBefore');
        const textAfter = getAdditionalInfoVal(fieldProps?.additionalInfo, 'textAfter');
        const text = getAdditionalInfoVal(fieldProps?.additionalInfo, 'text');

        const normalizedText = (
            <>
                {Lang(textBefore, language)}
                {" "}
                <a href={fieldProps.url} target="_blank" rel="noopener noreferrer">
                    {Lang(text, language)}
                </a>
                {" "}
                {Lang(textAfter, language)}
                {<LabelRedStar hide={!fieldProps?.isRequired}>*</LabelRedStar>}
            </>
        );


        return (
            <Controller
                name={fieldProps?.id}
                control={control}
                defaultValue={false}
                rules={{
                    validate: (value) => validateWithYupSchema(validation, value),
                }}
                render={({ field: { onChange, value } }) => (
                    <FlexBox direction="row" align="center">
                        <CustomCheckbox
                            id={fieldProps?.id}
                            name={fieldProps?.id}
                            checked={!!value}
                            onChange={() => onChange(!value)}
                        />
                        <span className="checkbox-label">{normalizedText}</span>
                    </FlexBox>
                )}
            />
        );
    }

    if ([FormFieldType.File].includes(fieldProps?.type)) {
        return (
            <FlexBox direction="column" gy={12} gyMobile={12} width="100%">
                <CommonLabel required={fieldProps?.isRequired} label={fieldLabel} />
                <Controller
                    name={fieldProps?.id}
                    control={control}
                    rules={{
                        validate: (value) => validateWithYupSchema(validation, value),
                    }}
                    render={({ field: { onChange, value } }) => {
                        const hasFile = hasUploadedFileValue(value);
                        return (
                            <FileInputWrapper customBackgroundColor={theme.projectEdit.imgContainerBg}>
                                <FileInput
                                    type="file"
                                    onChange={(ev: any) => {
                                        const files = ev?.target?.files;
                                        onChange(files?.length ? files : null);
                                    }}
                                />
                                <HolderContent>
                                    <FInter.SmallText color={theme.sendProjectsModal.title}>
                                        {hasFile
                                            ? t("competition_edit.form.message.file_uploaded")
                                            : t("competition_edit.form.message.file_upload")}
                                    </FInter.SmallText>
                                </HolderContent>
                            </FileInputWrapper>
                        );
                    }}
                />
            </FlexBox>
        );
    }

    if (fieldProps?.type === FormFieldType.SelectOne && options.length > 0) {
        return (
            <FlexBox direction="row">
                <CommonLabel required={fieldProps?.isRequired} label={fieldLabel} />
                <Controller
                    name={fieldProps?.id}
                    control={control}
                    rules={{
                        validate: (value) => validateWithYupSchema(validation, value),
                    }}
                    render={({ field: { onChange, value: controlledValue } }) => (
                        <FlexBox direction="column" directionMobile="column" gy={8} gyMobile={8}>
                            {options.map((item: any, currentIndex: number) => {
                                const title = getOptionTitle(item);
                                const isChecked = JSON.stringify(controlledValue) === JSON.stringify(item);

                                return (
                                    <Radio
                                        key={`${fieldProps?.id}_${currentIndex}`}
                                        checked={isChecked}
                                        title={title}
                                        onChange={() => onChange(item)}
                                    />
                                );
                            })}
                        </FlexBox>
                    )}
                />
            </FlexBox>
        );
    }

    if (fieldProps?.type === FormFieldType.SelectMultiple && options.length > 0) {
        return (
            <FlexBox direction="row">
                <CommonLabel required={fieldProps?.isRequired} label={fieldLabel} />
                <Controller
                    name={fieldProps?.id}
                    control={control}
                    rules={{
                        validate: (value) => validateWithYupSchema(validation, value),
                    }}
                    render={({ field: { onChange, value = [] } }) => (
                        <FlexBox direction="column" directionMobile="column" gy={8} gyMobile={8}>
                            {options.map((item: any, currentIndex: number) => {
                                const itemString = JSON.stringify(item);
                                const selectedValues = Array.isArray(value) ? value : [];
                                const isChecked = selectedValues.some((selected: any) => JSON.stringify(selected) === itemString);

                                return (
                                    <FlexBox key={`${fieldProps?.id}_${currentIndex}`} direction="row" align="center">
                                        <CustomCheckbox
                                            value={isChecked ? 1 : 0}
                                            onChange={() => {
                                                if (isChecked) {
                                                    onChange(selectedValues.filter((selected: any) => JSON.stringify(selected) !== itemString));
                                                    return;
                                                }
                                                onChange([...selectedValues, item]);
                                            }}
                                        />
                                        <span className="checkbox-label">{getOptionTitle(item)}</span>
                                    </FlexBox>
                                );
                            })}
                        </FlexBox>
                    )}
                />
            </FlexBox>
        );
    }

    if (fieldProps?.type === FormFieldType.Phone) {
        return (
            <FlexInputContainer direction="row" directionMobile="column">
                <Controller
                    name={fieldProps?.id}
                    control={control}
                    rules={{
                        validate: (value) => validateWithYupSchema(validation, value),
                    }}
                    render={({ field: { onChange, value = "" } }) => (
                        <CommonInput
                            {...fieldProps}
                            type={INPUT_TYPES.phone}
                            globalLang={language}
                            onChange={onChange}
                            onBlur={() => null}
                            onFocus={() => null}
                            name={fieldProps?.id}
                            value={value ?? "+7"}
                            required={fieldProps?.isRequired}
                            label={fieldLabel}
                            placeholder={fieldPlaceholder}
                        />
                    )}
                />
            </FlexInputContainer>
        );
    }

    if (fieldProps?.type === FormFieldType.Date) {
        return (
            <Controller
                name={fieldProps?.id}
                control={control}
                rules={{
                    validate: (value) => validateWithYupSchema(validation, value),
                }}
                render={({ field: { onChange, value, ref } }) => (
                    <CommonInput
                        {...fieldProps}
                        ref={ref}
                        type="date"
                        label={fieldLabel}
                        required={fieldProps?.isRequired}
                        style={{ ...(fieldProps?.hidden ? { display: "none" } : {}) }}
                        placeholder={fieldPlaceholder}
                        value={value ?? ""}
                        onChange={onChange}
                    />
                )}
            />
        );
    }

    return (
        <Controller
            name={fieldProps?.id}
            control={control}
            rules={{
                validate: (value) => validateWithYupSchema(validation, value),
            }}
            render={({ field: { onChange, value, ref } }) => (
                <CommonInput
                    {...fieldProps}
                    ref={ref}
                    type={
                        fieldProps?.type === FormFieldType.Mail ||
                        fieldProps?.type === FormFieldType.AdditionalMail
                            ? "email"
                            : "text"
                    }
                    label={fieldLabel}
                    placeholder={fieldPlaceholder}
                    style={{ ...(fieldProps?.hidden ? { display: "none" } : {}) }}
                    required={fieldProps?.isRequired}
                    value={value ?? ""}
                    onChange={onChange}
                />
            )}
        />
    );
};